import { Component, OnChanges, SimpleChanges, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { MultiSourceMultiValueWidget } from "../../base/MultiSourceMultiValueWidget";
import { TimeLapseWidget, IMultiSourceMultiValueWidget, SharedPoint, SharedSeries } from "src/app/models/dashboard";
import { SmartImageDetail, SmartImageListComponent } from 'src/app/components/smart-image-list/smart-image-list.component';
import { HttpClient } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { DataService, FileService } from 'src/api/services';
import { AbstractDataService } from 'src/service/data.service';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { CommandService } from 'src/api/services/command.service';
import { DashboardEventService } from '../../dashboard-event.service';

import * as moment from 'moment-timezone';
import { VideoService } from 'src/api/services';
import { DashboardDataCacheService } from 'src/service/dashboard-data-cache.service';
import { AzureAuthService } from 'src/service/azure-auth.service';

@Component({
  selector: 'app-time-lapse-widget',
  templateUrl: './time-lapse-widget.component.html',
  styleUrls: ['./time-lapse-widget.component.css']
})
export class TimeLapseWidgetComponent extends MultiSourceMultiValueWidget implements OnChanges {
  @ViewChild('imageSource') private imageSourceElement: ElementRef;
  @ViewChild('livestream') private livestream: ElementRef<HTMLImageElement>;
  @ViewChild('singleFeedList') private singleFeedList: SmartImageListComponent;

  private static readonly STREAM_TIMEOUT_MS = 100;
  private static readonly STREAM_CHECK_INTERVAL_MS = 100;
  private static readonly PLAYBACK_INTERVAL_MS = 500;
  private static readonly LOAD_DELAY_MS = 2000;

  public w: IMultiSourceMultiValueWidget;

  public isPlay = false;
  public isLoaded = false;
  public repeat = false;
  public images: SharedPoint[][];
  public selectedImage: SharedPoint[];
  public selectedImageIndex: number[];
  public width: string;
  public streamUrl: string | null = null;
  public imageUri: string;
  public liveStream = false;
  public latestImageUrl: string;
  public currentTime: Date = new Date();

  public overlays: Array<[number, number, number, number, string]> = [];

  /** Mapped image list for the single-feed <smart-image-list> binding. */
  public smartImageDetails: SmartImageDetail[] = [];

  private streamTimeout: any;
  public timeout = 0;
  private timer: any;

  constructor(
    fileService: FileService,
    dataService: AbstractDataService,
    cd: ChangeDetectorRef,
    app: ApplicationConfigService,
    cmd: CommandService,
    toasterService: ToastrService,
    http: HttpClient,
    cookieService: CookieService,
    data: DataService,
    events: DashboardEventService,
    auth: AzureAuthService,
    private videoService: VideoService,
    private _dashboardDataCache: DashboardDataCacheService
  ) {
    super(fileService, dataService, cd, app, cmd, toasterService, http, cookieService, data, events, auth, _dashboardDataCache);
    this.overlays = [];
  }

  override ngOnInit() {
    super.ngOnInit();
    this.streamUrl = null;
  }

  override ngOnDestroy() {
    this.stopLiveStream();
    this.singleFeedList?.stop();
    this.stop();
    super.ngOnDestroy();
  }

  showImage(i: number, ts: any): void {
    this.state = { index: i };
    this.toggleFullScreen();
  }

  liveDataOnly(): boolean {
    return false;
  }

  override eventTypes: string[] = ['timeTrigger'];
  override timeSeriesEvent(type: string, uri: string, data: any): void {
    if (type === 'timeTrigger') {
      for (let i = 0; i < this.w.timeSeriesList.length; i++) {
        let closestImageIndex = -1;
        let closestTimeDiff = Number.MAX_SAFE_INTEGER;
        for (let j = 0; j < this.images[i].length; j++) {
          const imageTime = new Date(this.images[i][j].timeStamp);
          const timeDiff = Math.abs(imageTime.getTime() - data.getTime());
          if (timeDiff < closestTimeDiff) {
            closestTimeDiff = timeDiff;
            closestImageIndex = j;
          }
        }
        if (closestImageIndex !== -1) {
          if (i === 0 && this.w.timeSeriesList.length === 1) {
            // Single-feed: drive navigation through SmartImageList
            this.singleFeedList?.selectImage(closestImageIndex);
          } else {
            this.selectedImageIndex[i] = closestImageIndex;
            this.changeImage(i);
          }
        }
      }
    }
  }

  widgetDataLoadTimeout(): void {
    // No timeout handling needed
  }

  override widgetDataLoadComplete(): void {
      
  }

  widgetDataLoaded(s: SharedSeries): void {
    console.log('TimeLapse', s);

    if ((this.widget as TimeLapseWidget).hideImagesWithoutMetaData) {
      s.values = s.values.filter(point => {
        if (!point.metaData?.analysis) {
          return false;
        }

        try {
          const analysisObj = JSON.parse(point.metaData.analysis);
          return analysisObj.objects?.length > 0;
        } catch (error) {
          console.error("Could not parse 'analysis' metadata:", error);
          return false;
        }
      });
    }

    if (s?.values?.length > 0) {
      this.imageUri = s.uri;
      this.images[s.index] = s.values;
      this.selectedImageIndex[s.index] = this.images[s.index].length - 1;
      this.selectedImage[s.index] = this.images[s.index][this.selectedImageIndex[s.index]];
      if (this.images[0]?.length > 0) {
        this.latestImageUrl = this.images[0][this.images[0].length - 1].value;
      }
    } else {
      this.images[s.index] = [];
    }

    if (s.index === 0) {
      this.updateSmartImageDetails();
    }

    setTimeout(() => this.isLoaded = true, TimeLapseWidgetComponent.LOAD_DELAY_MS);
  }

  toggleFullscreen() {
    this.fullScreen = !this.fullScreen;
  }

  toggleLiveStream(): void {
    this.liveStream = !this.liveStream;
    
    if (this.liveStream) {
      this.startLiveStream();
    } else {
      this.stopLiveStream();
    }
  }

  private startLiveStream(): void {
    const uri = encodeURIComponent(String(this.imageUri));
    this.videoService.VideoGetStreamKey({ customerId: this.app.customerId, uri }).subscribe({
      next: (streamKey) => {
        this.streamUrl = `/api/Video/GetSecureStream/${streamKey}`;
        this.timeout = 0;
        this.streamTimeout = setInterval(() => {
          this.currentTime = new Date();
          if (this.timeout >= TimeLapseWidgetComponent.STREAM_TIMEOUT_MS) {
            this.toasterService.info("Video stream timed out");
            this.toggleLiveStream();
          } else {
            this.timeout += 0.1;
          }
        }, TimeLapseWidgetComponent.STREAM_CHECK_INTERVAL_MS);
      },
      error: (err) => {
        this.toasterService.error("Failed to start video stream");
        this.liveStream = false;
        this.streamUrl = this.latestImageUrl;
        console.error("VideoGetStreamKey error:", err);
      }
    });
  }

  private stopLiveStream(): void {
    if (this.streamTimeout) {
      clearInterval(this.streamTimeout);
      this.streamTimeout = null;
      this.timeout = 0;
    }
    if (this.livestream?.nativeElement) {
      this.livestream.nativeElement.src = '';
    }
    this.streamUrl = this.latestImageUrl;
  }

  private isValidHttpUrl(urlString: string): boolean {
    try {
      const url = new URL(urlString);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  }

  widgetLiveDataLoaded(s: any): void {
    if (!s?.uri || !s?.point) return;

    const timeSeries = this.w.timeSeriesList.find(ts => ts.uri === s.uri);
    if (!timeSeries) return;

    const index = this.w.timeSeriesList.indexOf(timeSeries);
    if (index < 0 || index >= this.images.length) return;

    if (!this.isValidHttpUrl(s.point.value)) {
      // Update metadata only
      console.log("Updating MetaData Only", s.point.metaData);
      const lastIndex = this.images[index].length - 1;
      if (lastIndex >= 0 && this.images[index][lastIndex]) {
        this.images[index][lastIndex].metaData = s.point.metaData;
      }
    } else {
      const latest = {
        uri: s.uri,
        value: s.point.value,
        timeStamp: s.point.timeStamp,
        metaData: s.point.metaData
      } as SharedPoint;
      console.log("New Image:", latest.value);
      this.latestImageUrl = latest.value;

      if (this.images[index].length === 0) {
        this.images[index].push(latest);
        this.changeImage(index);
      } else {
        const last = this.images[index][this.images[index].length - 1];
        const lastDate = moment(last.timeStamp).toDate().setMilliseconds(0);
        const latestDate = moment(latest.timeStamp).toDate().setMilliseconds(0);
        
        if (lastDate < latestDate) {
          this.images[index].push(latest);
          this.images[index].shift();
          
          if (this.selectedImageIndex[index] === this.images[index].length - 1) {
            this.changeImage(index);
          }

          setTimeout(() => this.isLoaded = true, TimeLapseWidgetComponent.LOAD_DELAY_MS);
        }
      }
    }

    if (index === 0) {
      this.updateSmartImageDetails();
    }
  }


  widgetDataAdded(point: SharedPoint): void {
    // Not implemented
  }

  private updateSmartImageDetails(): void {
    this.smartImageDetails = (this.images[0] || []).map(p => ({
      src: p.value,
      overlay: p.metaData?.['analysis'] ?? null,
      timeStamp: p.timeStamp
    } as SmartImageDetail));
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Not implemented
  }

  widgetBeforeInit(): void {
    // No pre-initialization needed
    this.w = this.config as IMultiSourceMultiValueWidget;
    this.state = this.state ?? { index: 0 };
    this.width = `${Math.round(100 / this.w.timeSeriesList.length)}%`;
    
    const length = this.w.timeSeriesList.length;
    this.images = new Array(length);
    this.selectedImage = new Array(length);
    this.selectedImageIndex = new Array(length).fill(0);
    
    console.log('Widget initialized:', this.w);
  }

  widgetAfterInit(): void {
    
  }

  refresh(): void {
    this.ngOnInit();
  }

  play(): void {
    console.log('Starting playback');
    this.isPlay = true;
    this.selectedImageIndex[0] = 0;
    this.timer = setInterval(
      () => this.nextImage(),
      TimeLapseWidgetComponent.PLAYBACK_INTERVAL_MS
    );
  }

  stop(): void {
    this.isPlay = false;
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  getImageOverlay(i: number): string | null {
    return this.images?.[i]?.[this.selectedImageIndex?.[i]]?.metaData?.['analysis'] ?? null;
  }

  getSelectedImageOverlay(i: number): string | null {
    return this.selectedImage[i]?.metaData?.['analysis'] ?? null;
  }

  nextImage(): void {
    if (!this.images[0]?.length) return;

    let index = Math.min(this.images[0].length, this.selectedImageIndex[0] + 1);
    
    if (index === this.images[0].length) {
      if (this.repeat) {
        index = 0;
      } else {
        index = this.images[0].length - 1;
        this.stop();
      }
    }
    
    this.selectedImageIndex[0] = index;
    this.changeImage(0);
  }

  prevImage(): void {
    if (!this.images[0]?.length) return;

    let index = this.selectedImageIndex[0] - 1;
    if (index < 0) {
      index = this.images[0].length - 1;
    }
    
    this.selectedImageIndex[0] = index;
    this.changeImage(0);
  }

  private changeImage(index: number): void {
    if (this.images[index]?.[this.selectedImageIndex[index]]) {
      this.selectedImage[index] = this.images[index][this.selectedImageIndex[index]];
    }
  }

  selectImage(image: SharedPoint): void {
    this.selectedImage[0] = image;
    
    if (this.imageSourceElement?.nativeElement) {
      this.imageSourceElement.nativeElement.load();
    }

    if (image.metaData?.['analysis'] && image.metaData['content-type'] === 'image/jpeg') {
      this.overlays = [];
      
      try {
        const imageOverlay = JSON.parse(image.metaData['analysis']);
        const img = new Image();
        
        img.onload = (event: Event) => {
          const target = event.target as HTMLImageElement;
          const imgElement = document.getElementById('imgSource');
          
          if (imgElement && imageOverlay.objects) {
            const imgWidth = target.width;
            const imgHeight = target.height;
            
            for (const obj of imageOverlay.objects) {
              const startX = (obj.rectangle.x / imgWidth) * 100;
              const startY = (obj.rectangle.y / imgHeight) * 100;
              const width = (obj.rectangle.w / imgWidth) * 100;
              const height = (obj.rectangle.h / imgHeight) * 100;
              this.overlays.push([startX, startY, width, height, obj.object]);
            }
            console.log('Overlays calculated:', this.overlays);
          }
        };
        
        img.src = image.value;
      } catch (error) {
        console.error('Failed to parse image analysis metadata:', error);
      }
    }
  }

  getBearings(): number[] {
    const bearings: number[] = [];
    const widget = this.widget as any;
    const bearing = widget.bearing;
    const viewAngle = widget.viewAngle;
    const imageViewAngle = viewAngle / this.images.length;
    const viewAngleStepSize = imageViewAngle / 3;
    const start = bearing - (viewAngle / 2);
    const steps = this.images.length * 3;
    const startOffset = viewAngleStepSize / 2;
    
    for (let i = 0; i < steps; i++) {
      let b = i * viewAngleStepSize + start + startOffset;
      
      // Normalize bearing to 0-360 range
      if (b < 0) {
        b += 360;
      }
      
      bearings.push(b);
    }
    
    return bearings;
  }
}
